import { BcmNetworkPrinter } from '@shared/models/network-printer';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { PrinterType } from '@shared/models/receipt-data';

export interface TenantModuleLicenseView {
    permission: BcmTenantPermission | string;
    quota: number | null;
    activatedUntil?: string | null;
}

export interface PosPrinterLicenseUsageView {
    escpos: number;
    eposXml: number;
}

export type PosReceiptPrinterType = Extract<PrinterType, 'giant' | 'epson'>;

export function mapReceiptPrinterTypeToModulePermission(
    printerType: string | null | undefined,
): BcmTenantPermission | null {
    if (printerType === 'giant') {
        return BcmTenantPermission.POS_PRINTER_ESCPOS;
    }
    if (printerType === 'epson') {
        return BcmTenantPermission.POS_PRINTER_EPOS_XML;
    }
    return null;
}

export function getModuleLicense(
    moduleLicenses: TenantModuleLicenseView[] | undefined,
    permission: BcmTenantPermission,
): TenantModuleLicenseView | undefined {
    return (moduleLicenses ?? []).find((entry) => entry.permission === permission);
}

export function hasActiveModuleLicense(
    moduleLicenses: TenantModuleLicenseView[] | undefined,
    permission: BcmTenantPermission,
): boolean {
    const license = getModuleLicense(moduleLicenses, permission);
    return (license?.quota ?? 0) > 0;
}

export function getReceiptPrinterUsage(
    usage: PosPrinterLicenseUsageView | undefined,
    printerType: PosReceiptPrinterType,
): number {
    if (!usage) {
        return 0;
    }
    return printerType === 'giant' ? usage.escpos : usage.eposXml;
}

export function canAddReceiptPrinterOfType(
    moduleLicenses: TenantModuleLicenseView[] | undefined,
    usage: PosPrinterLicenseUsageView | undefined,
    printerType: PosReceiptPrinterType,
): boolean {
    const permission = mapReceiptPrinterTypeToModulePermission(printerType);
    if (!permission || !hasActiveModuleLicense(moduleLicenses, permission)) {
        return false;
    }

    const quota = getModuleLicense(moduleLicenses, permission)?.quota ?? 0;
    return getReceiptPrinterUsage(usage, printerType) < quota;
}

export function isReceiptPrinterLicensed(
    moduleLicenses: TenantModuleLicenseView[] | undefined,
    printer: Pick<BcmNetworkPrinter, 'printerType'>,
): boolean {
    const permission = mapReceiptPrinterTypeToModulePermission(printer.printerType);
    if (!permission) {
        return true;
    }
    return hasActiveModuleLicense(moduleLicenses, permission);
}

export function hasAnyPosPrinterModuleLicense(
    moduleLicenses: TenantModuleLicenseView[] | undefined,
): boolean {
    return hasActiveModuleLicense(moduleLicenses, BcmTenantPermission.POS_PRINTER_ESCPOS)
        || hasActiveModuleLicense(moduleLicenses, BcmTenantPermission.POS_PRINTER_EPOS_XML);
}

export function areAllLicensedReceiptPrinterTypesAtQuota(
    moduleLicenses: TenantModuleLicenseView[] | undefined,
    usage: PosPrinterLicenseUsageView | undefined,
): boolean {
    const escposLicensed = hasActiveModuleLicense(moduleLicenses, BcmTenantPermission.POS_PRINTER_ESCPOS);
    const eposLicensed = hasActiveModuleLicense(moduleLicenses, BcmTenantPermission.POS_PRINTER_EPOS_XML);

    if (!escposLicensed && !eposLicensed) {
        return false;
    }

    const escposBlocked = !escposLicensed
        || !canAddReceiptPrinterOfType(moduleLicenses, usage, 'giant');
    const eposBlocked = !eposLicensed
        || !canAddReceiptPrinterOfType(moduleLicenses, usage, 'epson');

    return escposBlocked && eposBlocked;
}

export function filterUsableReceiptPrinters(
    printers: BcmNetworkPrinter[],
    moduleLicenses: TenantModuleLicenseView[] | undefined,
    userId: string,
): BcmNetworkPrinter[] {
    return (printers ?? []).filter((printer) =>
        printer.isReceiptPrinter
        && (printer.assignedUserIds ?? []).includes(userId)
        && isReceiptPrinterLicensed(moduleLicenses, printer),
    );
}

export function resolveReceiptPrinterTypeForForm(
    existingPrinterType: string | null | undefined,
    moduleLicenses: TenantModuleLicenseView[] | undefined,
    usage: PosPrinterLicenseUsageView | undefined,
): PosReceiptPrinterType {
    if (existingPrinterType === 'giant' || existingPrinterType === 'epson') {
        return existingPrinterType;
    }

    if (canAddReceiptPrinterOfType(moduleLicenses, usage, 'giant')) {
        return 'giant';
    }
    if (canAddReceiptPrinterOfType(moduleLicenses, usage, 'epson')) {
        return 'epson';
    }
    if (hasActiveModuleLicense(moduleLicenses, BcmTenantPermission.POS_PRINTER_ESCPOS)) {
        return 'giant';
    }
    if (hasActiveModuleLicense(moduleLicenses, BcmTenantPermission.POS_PRINTER_EPOS_XML)) {
        return 'epson';
    }

    return 'giant';
}

export function getPrinterTypeOptionDisabledReason(
    moduleLicenses: TenantModuleLicenseView[] | undefined,
    usage: PosPrinterLicenseUsageView | undefined,
    printerType: PosReceiptPrinterType,
): 'missingLicense' | 'quotaExceeded' | null {
    const permission = mapReceiptPrinterTypeToModulePermission(printerType);
    if (!permission) {
        return null;
    }

    if (!hasActiveModuleLicense(moduleLicenses, permission)) {
        return 'missingLicense';
    }

    if (!canAddReceiptPrinterOfType(moduleLicenses, usage, printerType)) {
        return 'quotaExceeded';
    }

    return null;
}
